import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { CalendarIcon, Clock, AlertTriangle, Ban, Sparkles, DollarSign } from "lucide-react";
import { format, isSameDay, addMinutes, parse } from "date-fns";
import { formatInTimeZone, toZonedTime, fromZonedTime } from "date-fns-tz";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { PaymentForm } from "@/components/PaymentForm";
import { CoachSelector } from "@/components/CoachSelector";
import { User, CheckCircle2 } from "lucide-react";
import { getStripePublishableKey } from "@/lib/stripe-config";

const stripePromise = loadStripe(getStripePublishableKey());

interface Coach {
  id: string;
  full_name: string;
  avatar_url: string | null;
  email: string;
}

const Booking = () => {
  const navigate = useNavigate();
  const [date, setDate] = useState<Date | undefined>(new Date());
  const [duration, setDuration] = useState<number[]>([45]);
  const [selectedTime, setSelectedTime] = useState<string>("");
  const [customerNotes, setCustomerNotes] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [blockedTimes, setBlockedTimes] = useState<Array<{ blocked_date: string; start_time: string; end_time: string }>>([]);
  const [paymentIntentId, setPaymentIntentId] = useState<string>("");
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [selectedCoachId, setSelectedCoachId] = useState<string | null>(null);
  const [coaches, setCoaches] = useState<Coach[]>([]);

  useEffect(() => {
    fetchCoaches();
  }, []);

  useEffect(() => {
    fetchBlockedTimes();
  }, [date, selectedCoachId]);

  const fetchCoaches = async () => {
    try {
      const { data: coachRoles, error: rolesError } = await supabase
        .from("user_roles")
        .select("user_id")
        .eq("role", "coach");

      if (rolesError) throw rolesError;

      if (!coachRoles || coachRoles.length === 0) {
        setCoaches([]);
        return;
      }

      const coachIds = coachRoles.map(r => r.user_id);

      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url, email")
        .in("id", coachIds);

      if (profilesError) throw profilesError;

      setCoaches(profiles || []);

      // Auto-select if only one coach
      if (profiles && profiles.length === 1) {
        setSelectedCoachId(profiles[0].id);
      }
    } catch (error) {
      console.error("Error fetching coaches:", error);
    }
  };

  const fetchBlockedTimes = async () => {
    if (!date || !selectedCoachId) {
      setBlockedTimes([]);
      return;
    }

    try {
      const dateStr = format(date, "yyyy-MM-dd");
      const { data, error } = await supabase
        .from("blocked_times")
        .select("blocked_date, start_time, end_time")
        .eq("coach_id", selectedCoachId)
        .eq("blocked_date", dateStr);

      if (error) throw error;
      setBlockedTimes(data || []);
    } catch (error) {
      console.error("Error fetching blocked times:", error);
    }
  };

  const calculatePrice = (minutes: number) => {
    // Rate: $1.99 per minute
    const ratePerMinute = 1.99;
    return (minutes * ratePerMinute).toFixed(2);
  };

  const timeSlots = [
    { value: "09:00", label: "9:00 AM" },
    { value: "10:00", label: "10:00 AM" },
    { value: "11:00", label: "11:00 AM" },
    { value: "12:00", label: "12:00 PM" },
    { value: "13:00", label: "1:00 PM" },
    { value: "14:00", label: "2:00 PM" },
    { value: "15:00", label: "3:00 PM" },
    { value: "16:00", label: "4:00 PM" },
    { value: "17:00", label: "5:00 PM" },
    { value: "18:00", label: "6:00 PM" },
    { value: "19:00", label: "7:00 PM" },
    { value: "20:00", label: "8:00 PM" },
    { value: "21:00", label: "9:00 PM" },
    { value: "22:00", label: "10:00 PM" },
  ];

  const isTimeSlotAvailable = (timeSlot: string) => {
    if (!date) return false;

    // Check if time slot is blocked by coach
    const isBlocked = blockedTimes.some((blocked) => {
      const slotHour = parseInt(timeSlot.split(":")[0]);
      const startHour = parseInt(blocked.start_time.split(":")[0]);
      const endHour = parseInt(blocked.end_time.split(":")[0]);
      return slotHour >= startHour && slotHour < endHour;
    });

    if (isBlocked) return false;

    // Check if time slot is in the past or within 1.5 hours of current time
    const now = new Date();
    const isToday = isSameDay(date, now);
    
    if (isToday) {
      const slotDateTime = parse(timeSlot, "HH:mm", date);
      const minBookingTime = addMinutes(now, 90); // 1.5 hours from now
      
      if (slotDateTime <= minBookingTime) {
        return false;
      }
    }

    return true;
  };

  const handlePaymentMethodCreated = async (paymentIntentIdFromStripe: string) => {
    setPaymentIntentId(paymentIntentIdFromStripe);
    await handleBooking(paymentIntentIdFromStripe);
  };

  const handleBooking = async (paymentIntentIdValue: string) => {
    if (!date || !selectedTime) {
      toast.error("Please select both date and time");
      return;
    }

    setIsSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error("Please log in to book a lesson");
        navigate("/auth");
        return;
      }

      // Get user's timezone
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // Convert selected local time to UTC
      const localDateTimeStr = `${format(date, "yyyy-MM-dd")}T${selectedTime}`;
      const localDateTime = new Date(localDateTimeStr);
      
      // Create UTC ISO string for storage
      const bookingDatetimeUTC = localDateTime.toISOString();

      const { data: booking, error } = await supabase.from("bookings")
        .insert([{
          user_id: user.id,
          coach_id: selectedCoachId,
          booking_date: format(date, "yyyy-MM-dd"),
          booking_time: selectedTime,
          booking_datetime: bookingDatetimeUTC,
          booking_timezone: userTimezone,
          duration_minutes: duration[0],
          price: parseFloat(calculatePrice(duration[0])),
          status: "pending",
          payment_intent_id: paymentIntentIdValue,
          payment_status: "pending",
          customer_notes: customerNotes || null
        }])
        .select()
        .single();

      if (error) throw error;

      // Send booking notification emails
      try {
        const { data: profile } = await supabase
          .from("profiles")
          .select("full_name, email")
          .eq("id", user.id)
          .single();

        // Get coach name if a coach was selected
        let coachName = undefined;
        if (selectedCoachId) {
          const { data: coachProfile } = await supabase
            .from("profiles")
            .select("full_name")
            .eq("id", selectedCoachId)
            .maybeSingle();
          coachName = coachProfile?.full_name;
        }

        // Notify customer
        await supabase.functions.invoke('send-booking-notification', {
          body: {
            customerEmail: profile?.email || user.email,
            customerName: profile?.full_name || "Customer",
            bookingDatetime: bookingDatetimeUTC,
            bookingTimezone: userTimezone,
            duration: duration[0],
            price: parseFloat(calculatePrice(duration[0])),
            status: "pending",
            bookingId: booking?.id,
            coachName: coachName
          },
        });

        // Notify coaches
        if (booking) {
          await supabase.functions.invoke('notify-coach-booking', {
            body: {
              bookingId: booking.id,
              customerName: profile?.full_name || "Customer",
              bookingDatetime: bookingDatetimeUTC,
              bookingTimezone: userTimezone,
              duration: duration[0],
              customerNotes: customerNotes || ""
            },
          });
        }
      } catch (emailError) {
        console.error("Error sending email:", emailError);
        // Don't block booking if email fails
      }

      toast.success("Booking created! You'll be charged when a coach accepts.");
      navigate("/progress");
    } catch (error) {
      console.error("Booking error:", error);
      toast.error("Failed to book lesson. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProceedToPayment = () => {
    if (!selectedCoachId) {
      toast.error("Please select a coach");
      return;
    }
    if (!date || !selectedTime) {
      toast.error("Please select both date and time");
      return;
    }
    setShowPaymentForm(true);
  };

  const availableTimeSlots = timeSlots.filter(slot => isTimeSlotAvailable(slot.value));

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background to-muted/30 py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-5xl md:text-6xl font-bold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              Book Your Vocal Lesson
            </h1>
            <p className="text-xl text-muted-foreground">Customize your session and choose your preferred time</p>
          </div>

          {/* Cancellation Policy Alert */}
          <Alert className="mb-8 border-amber-500/50 bg-amber-500/10">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            <AlertDescription className="text-sm">
              <strong>Cancellation Policy:</strong> Cancel at least 24 hours before your lesson to avoid a $15.00 no-show fee.
            </AlertDescription>
          </Alert>

          <div className="space-y-8">
            {/* Coach Selection - Only show when multiple coaches */}
            {selectedCoachId && coaches.length === 1 ? (
              <Card className="border-2 border-primary/30 shadow-xl bg-gradient-to-br from-card via-card to-primary/5">
                <CardHeader>
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <div className="absolute inset-0 bg-gradient-to-br from-primary to-secondary rounded-full blur-md opacity-50"></div>
                      <div className="relative h-16 w-16 rounded-full border-3 border-primary/30 overflow-hidden bg-card">
                        {coaches[0].avatar_url ? (
                          <img 
                            src={coaches[0].avatar_url} 
                            alt={coaches[0].full_name}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-primary/20 to-secondary/20">
                            <User className="w-8 h-8 text-primary" />
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-2xl">Your Coach</CardTitle>
                      <CardDescription className="text-base font-medium text-foreground/80">
                        {coaches[0].full_name}
                      </CardDescription>
                    </div>
                    <Badge className="ml-auto" variant="default">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Assigned
                    </Badge>
                  </div>
                </CardHeader>
              </Card>
            ) : (
              <CoachSelector
                selectedCoachId={selectedCoachId}
                onCoachSelect={setSelectedCoachId}
              />
            )}

            {/* Duration Selection */}
            <Card className="border-2 border-primary/30 shadow-2xl bg-gradient-to-br from-card via-card to-primary/5">
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">Session Duration</CardTitle>
                    <CardDescription>Choose how long you'd like to practice</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-6">
                  <div className="space-y-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-muted-foreground">Duration</span>
                      <span className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                        {duration[0]} min
                      </span>
                    </div>
                    
                    <Slider
                      value={duration}
                      onValueChange={setDuration}
                      min={15}
                      max={90}
                      step={15}
                      className="py-4"
                    />
                    
                    <div className="flex justify-between text-[10px] xs:text-xs text-muted-foreground px-1">
                      <span>15 min</span>
                      <span>30 min</span>
                      <span>45 min</span>
                      <span>60 min</span>
                      <span>90 min</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-5 gap-2">
                    {[15, 30, 45, 60, 90].map((mins) => (
                      <Button
                        key={mins}
                        variant={duration[0] === mins ? "default" : "outline"}
                        size="sm"
                        onClick={() => setDuration([mins])}
                        className="w-full"
                      >
                        {mins}m
                      </Button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Date & Time Selection */}
            <Card className="border-border/40 shadow-xl">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                    <CalendarIcon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">Select Date & Time</CardTitle>
                    <CardDescription>Choose when you'd like your lesson</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid lg:grid-cols-[1fr,1.2fr] gap-8">
                   {/* Calendar */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm text-muted-foreground flex items-center gap-2">
                      <CalendarIcon className="w-4 h-4 text-primary" />
                      Select Date
                    </h4>
                    <div className="relative bg-gradient-to-br from-primary/10 via-secondary/5 to-primary/5 rounded-2xl p-6 border-2 border-primary/20 shadow-2xl backdrop-blur-sm">
                      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent rounded-2xl"></div>
                      <div className="relative w-full flex justify-center">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={setDate}
                          disabled={(date) => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            const checkDate = new Date(date);
                            checkDate.setHours(0, 0, 0, 0);
                            return checkDate < today;
                          }}
                          className="pointer-events-auto w-full rounded-xl border-0 bg-card/50 backdrop-blur-sm p-4 shadow-inner"
                          classNames={{
                            months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                            month: "space-y-4",
                            caption: "flex justify-center pt-1 relative items-center text-foreground font-semibold",
                            caption_label: "text-base font-bold",
                            nav: "space-x-1 flex items-center",
                            nav_button: "h-8 w-8 bg-primary/10 hover:bg-primary/20 transition-colors rounded-lg",
                            nav_button_previous: "absolute left-1",
                            nav_button_next: "absolute right-1",
                            table: "w-full border-collapse space-y-1",
                            head_row: "flex",
                            head_cell: "text-muted-foreground rounded-md w-9 font-semibold text-[0.8rem]",
                            row: "flex w-full mt-2",
                            cell: "text-center text-sm p-0 relative [&:has([aria-selected])]:bg-primary/20 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
                            day: "h-9 w-9 p-0 font-medium hover:bg-primary/10 hover:scale-110 transition-all aria-selected:bg-primary aria-selected:text-primary-foreground rounded-lg",
                            day_selected: "bg-gradient-to-br from-primary to-secondary text-white hover:from-primary/90 hover:to-secondary/90 shadow-lg",
                            day_today: "bg-secondary/20 text-secondary-foreground font-bold ring-2 ring-secondary",
                            day_outside: "text-muted-foreground opacity-30",
                            day_disabled: "text-muted-foreground opacity-20",
                            day_hidden: "invisible",
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Time Slots */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-sm text-muted-foreground flex items-center gap-2">
                        <Clock className="w-4 h-4 text-primary" />
                        Select Time
                      </h4>
                      {selectedTime && (
                        <span className="text-xs bg-primary/10 text-primary px-3 py-1 rounded-full font-medium">
                          {timeSlots.find(s => s.value === selectedTime)?.label}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Times in your local timezone • Book at least 1.5 hours in advance
                    </p>
                    
                    {availableTimeSlots.length === 0 ? (
                      <Alert className="border-destructive/50 bg-destructive/10">
                        <Ban className="h-4 w-4 text-destructive" />
                        <AlertDescription className="text-sm">
                          No available time slots for this date. Please select a different date.
                        </AlertDescription>
                      </Alert>
                    ) : (
                      <>
                        {blockedTimes.length > 0 && (
                          <Alert className="border-amber-500/50 bg-amber-500/10">
                            <AlertTriangle className="h-4 w-4 text-amber-500" />
                            <AlertDescription className="text-xs">
                              Some time slots are unavailable due to coach scheduling
                            </AlertDescription>
                          </Alert>
                        )}
                        <div className="grid grid-cols-3 gap-2 max-h-[400px] overflow-y-auto pr-2">
                          {availableTimeSlots.map((slot) => (
                            <Button
                              key={slot.value}
                              variant={selectedTime === slot.value ? "default" : "outline"}
                              size="sm"
                              onClick={() => setSelectedTime(slot.value)}
                              className="transition-all hover:scale-105"
                            >
                              {slot.label}
                            </Button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Customer Notes */}
            <Card className="border-border/40 shadow-xl">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">Additional Notes</CardTitle>
                    <CardDescription>Share any goals or requests (optional)</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Textarea
                  id="customer-notes"
                  placeholder="Share any specific goals, questions, or requests for your coach..."
                  value={customerNotes}
                  onChange={(e) => setCustomerNotes(e.target.value)}
                  className="min-h-[120px] resize-none"
                  maxLength={500}
                />
                <p className="text-xs text-muted-foreground mt-2">
                  {customerNotes.length}/500 characters
                </p>
              </CardContent>
            </Card>

            {/* Price Summary - Bottom */}
            <Card className="border-2 border-primary/20 shadow-2xl">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-white" />
                  </div>
                  <CardTitle>Price Summary</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-8">
                  <div className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl p-6 border border-primary/20">
                    <div className="text-center space-y-3">
                      <p className="text-sm text-muted-foreground">Total Price</p>
                      <p className="text-6xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                        ${calculatePrice(duration[0])}
                      </p>
                      <p className="text-xs text-muted-foreground">+ $15 no-show fee if cancelled late</p>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm flex flex-col justify-center">
                    <div className="flex items-center justify-between py-2 border-b border-border/50">
                      <span className="text-muted-foreground">Rate per minute</span>
                      <span className="font-semibold">$1.99</span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-border/50">
                      <span className="text-muted-foreground">Duration</span>
                      <span className="font-semibold">{duration[0]} minutes</span>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-muted-foreground">Lesson price</span>
                      <span className="font-bold text-lg">${calculatePrice(duration[0])}</span>
                    </div>
                  </div>
                </div>

                {!showPaymentForm ? (
                  <div className="space-y-4">
                    <Alert className="bg-primary/5 border-primary/20">
                      <CheckCircle2 className="h-4 w-4 text-primary" />
                      <AlertDescription className="text-sm">
                        <strong>Payment authorization only.</strong> Your card will be authorized for ${calculatePrice(duration[0])} but <strong>not charged</strong> until your coach accepts the lesson request.
                      </AlertDescription>
                    </Alert>
                    <Button 
                      variant="hero"
                      size="lg"
                      className="w-full text-lg py-6"
                      onClick={handleProceedToPayment}
                      disabled={!date || !selectedTime}
                    >
                      Continue to Payment
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <Alert className="bg-primary/5 border-primary/20">
                      <CheckCircle2 className="h-4 w-4 text-primary" />
                      <AlertDescription className="text-sm">
                        <strong>You won't be charged yet.</strong> We'll authorize your payment method now, but you'll only be charged once your coach accepts the lesson request.
                      </AlertDescription>
                    </Alert>
                    <Elements stripe={stripePromise}>
                      <PaymentForm
                        amount={parseFloat(calculatePrice(duration[0]))}
                        onPaymentMethodCreated={handlePaymentMethodCreated}
                        isSubmitting={isSubmitting}
                      />
                    </Elements>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Booking;
